/**
 * Tiny framework-free DOM helpers.
 *
 * Just enough to build the SPA views declaratively without a UI framework,
 * keeping runtime dependencies at zero (design: framework-free responsive web
 * app). `el` creates elements with typed props/children; `clear` empties a node.
 */

/** Properties accepted by {@link el}. */
export interface ElProps {
  class?: string;
  text?: string;
  html?: string;
  type?: string;
  value?: string;
  placeholder?: string;
  name?: string;
  href?: string;
  disabled?: boolean;
  /** Extra `[attr]=value` pairs (e.g. aria-*, inputmode, step, min, max). */
  attrs?: Record<string, string>;
  /** Event listeners keyed by event name (e.g. click, submit, input). */
  on?: Partial<Record<keyof HTMLElementEventMap, (event: Event) => void>>;
}

/** A child node: an element, a string (text node), or nothing. */
export type Child = Node | string | null | undefined | false;

/**
 * Create an element with props and children. Strings become text nodes;
 * falsy children are skipped so conditional rendering reads cleanly.
 */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: ElProps = {},
  children: Child[] = []
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  if (props.class !== undefined) node.className = props.class;
  if (props.text !== undefined) node.textContent = props.text;
  if (props.html !== undefined) node.innerHTML = props.html;
  if (props.type !== undefined) node.setAttribute("type", props.type);
  if (props.name !== undefined) node.setAttribute("name", props.name);
  if (props.placeholder !== undefined)
    node.setAttribute("placeholder", props.placeholder);
  if (props.href !== undefined) node.setAttribute("href", props.href);
  if (props.value !== undefined) {
    (node as unknown as { value: string }).value = props.value;
  }
  if (props.disabled === true) {
    node.setAttribute("disabled", "");
  }
  if (props.attrs !== undefined) {
    for (const [key, val] of Object.entries(props.attrs)) {
      node.setAttribute(key, val);
    }
  }
  if (props.on !== undefined) {
    for (const [event, handler] of Object.entries(props.on)) {
      if (handler !== undefined) {
        node.addEventListener(event, handler as EventListener);
      }
    }
  }

  for (const child of children) {
    if (child === null || child === undefined || child === false) {
      continue;
    }
    node.append(child);
  }
  return node;
}

/** Remove all children from a node. */
export function clear(node: Node): void {
  while (node.firstChild !== null) {
    node.removeChild(node.firstChild);
  }
}

/** Replace all children of `node` with `children`. */
export function render(node: Element, ...children: Child[]): void {
  clear(node);
  for (const child of children) {
    if (child === null || child === undefined || child === false) {
      continue;
    }
    node.append(child);
  }
}
